/**
 * Who stands behind the rankings.
 *
 * E-E-A-T and AI citation both want to know who is responsible for a ranked
 * list. A page that orders 222 businesses and names nobody is the easiest kind
 * of page for a model to skip, and the hardest for a reader to argue with or
 * complain to.
 *
 * ## What the byline claims, and what it does not
 *
 * The rankings are computed from a published rubric, not hand-written. So the
 * byline deliberately claims authorship of *the method and the standards*
 * rather than of each verdict. "Methodology and editorial standards by Brad
 * Williams" is true; "reviewed by Brad Williams" would imply someone
 * personally assessed every listing on every page, which is not what happens
 * and is exactly the kind of overclaim the citation gate exists to prevent
 * elsewhere.
 *
 * ## Why a real person
 *
 * An invented persona was considered and rejected. On a site whose entire
 * pitch is that its claims are grounded and checkable, a fabricated editor is
 * the one lie that would discredit everything else, and it is trivially
 * discoverable.
 *
 * ## Why a constant rather than a table
 *
 * There is one author. A constant renders identical Person markup, needs no
 * migration, and cannot drift from the pages that use it. The rebuild plan
 * proposes an Author model for when there are several, at which point this
 * module becomes the seed for that table and the accessor keeps its shape.
 */

export interface SiteAuthor {
	name: string
	/** Role as it appears next to the name. */
	title: string
	/** One or two sentences. Rendered on /scoring-method, not on every page. */
	bio: string
	/** Stable identifier for the Person node, so references can point at it. */
	id: string
	/** Profiles that corroborate the person. Empty until they exist. */
	sameAs: string[]
}

export const SITE_AUTHOR: SiteAuthor = {
	name: 'Brad Williams',
	title: 'Editor, DFW Professionals',
	bio: 'Brad Williams built and maintains the DFW Professionals scoring rubric, the review-analysis pipeline behind it, and the correction process for businesses that believe a listing is wrong.',
	id: '#author',
	sameAs: [],
}

/** How the byline reads wherever it appears. Never "reviewed by". */
export const AUTHOR_BYLINE = `Methodology and editorial standards by ${SITE_AUTHOR.name}`

/**
 * The Person node.
 *
 * Emitted with an `@id` so ranking pages can reference the same person rather
 * than restating them, which is what lets a crawler treat one author across
 * many pages as one entity instead of many.
 */
export function buildPersonLd({ origin }: { origin: string }): string {
	return JSON.stringify({
		'@context': 'https://schema.org',
		'@type': 'Person',
		'@id': `${origin}/scoring-method${SITE_AUTHOR.id}`,
		name: SITE_AUTHOR.name,
		jobTitle: SITE_AUTHOR.title,
		description: SITE_AUTHOR.bio,
		url: `${origin}/scoring-method`,
		worksFor: { '@type': 'Organization', name: 'DFW Professionals', url: origin },
		...(SITE_AUTHOR.sameAs.length > 0 ? { sameAs: SITE_AUTHOR.sameAs } : {}),
	})
}

/** A reference to the Person node above, for embedding as an `author`. */
export function authorReference({ origin }: { origin: string }) {
	return {
		'@type': 'Person' as const,
		'@id': `${origin}/scoring-method${SITE_AUTHOR.id}`,
		name: SITE_AUTHOR.name,
	}
}
